'use es6';

import { PureComponent, Fragment } from 'react';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Spacer } from 'foundations-components/components/Spacer';
import { hasEmailDomainBlockingAccess } from 'FormsUI/selectors/Scope';
import { getPropertyObjectTypeId } from 'FormsUI/utils/PropertiesUtils';
import { CommentableArea } from 'collaboration-sidebar';
import DocLink from 'ui-forms-components/links/DocLink';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { LockBadge } from 'foundations-components/transitional/components/LockBadge';
import debounce from 'hs-lodash/debounce';
import { EDITED_DEFAULT_VALUE, EDITED_EMAIL_DOMAINS_TO_BLOCK } from 'FormsUI/constants/TrackingInteractionKeys/Editor/FormTab';
import { trackEditorInteraction } from 'FormsUI/utils/TrackingUtils';
import { WHAT_DOMAINS_ARE_BLOCKED } from 'FormsCommonComponents/constants/KCSArticlePaths';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import updateFormFieldKey from 'forms-utils-lib/formField/utils/updateFormFieldKey';
import FormFieldKeys from 'forms-utils-lib/constants/formField/FormFieldKeys';
import updateValidation from 'forms-utils-lib/formField/utils/updateValidation';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const langKey = suffix => `editor.form.config.${suffix}`;
const WhatDomainsAreBlockedComponent = () => /*#__PURE__*/_jsx(DocLink, {
  path: WHAT_DOMAINS_ARE_BLOCKED,
  children: /*#__PURE__*/_jsx(FormattedMessage, {
    message: langKey('seeList')
  })
});
export const TextComponent = ({
  hasDomainBlocking = false,
  validation = {},
  onChangeTextInput = () => {},
  onChangeCheckbox = () => {},
  field
}) => {
  const formLabel = !hasDomainBlocking ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: langKey('emailDomainsToBlock')
    }), /*#__PURE__*/_jsx(LockBadge, {
      marginInlineStart: "200"
    })]
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: langKey('emailDomainsToBlock')
  });
  return /*#__PURE__*/_jsx(CommentableArea, {
    moduleId: `${getPropertyObjectTypeId(field)}-email-domains-to-block`,
    description: {
      translationKey: 'formsEmailDomainsToBlock'
    },
    children: /*#__PURE__*/_jsxs("div", {
      "data-test-id": "config-input-field",
      children: [/*#__PURE__*/_jsx(FormControl, {
        label: formLabel,
        tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
          message: langKey('emailDomainsToBlockToolTip')
        }),
        tooltipPlacement: "top right",
        children: /*#__PURE__*/_jsx(TextInput, {
          defaultValue: validation.data,
          use: "on-dark",
          onChange: onChangeTextInput,
          "data-editable-field": "blocked-email-domains"
        })
      }), /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(Checkbox, {
          checked: validation.useDefaultBlockList,
          onChange: onChangeCheckbox,
          "data-editable-field": "blocked-use-default-domains",
          "data-test-id": "config-input-field-email-block-list",
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: langKey('blockFreeEmailProviders'),
            options: {
              seeList: /*#__PURE__*/_jsx(WhatDomainsAreBlockedComponent, {})
            }
          })
        })
      })]
    })
  });
};
const UpgradeModal = ({
  field
}) => {
  const {
    openUpgradeModal
  } = useUpgradeModal('forms-email-domain-blocking');
  return /*#__PURE__*/_jsx(TextComponent, {
    field: field,
    validation: field.validation,
    onChangeTextInput: openUpgradeModal,
    onChangeCheckbox: openUpgradeModal
  });
};
class Text extends PureComponent {
  maybeRenderDomainBlockField() {
    const {
      field,
      update,
      hasDomainBlocking
    } = this.props;
    if (field.name !== 'email') {
      return null;
    }
    if (!hasDomainBlocking && !field.validation.useDefaultBlockList) {
      return /*#__PURE__*/_jsx(UpgradeModal, {
        field: field
      });
    }
    return /*#__PURE__*/_jsx(TextComponent, {
      field: field,
      hasDomainBlocking: hasDomainBlocking,
      validation: field.validation,
      onChangeTextInput: ({
        target: {
          value
        }
      }) => {
        debounce(() => trackEditorInteraction(EDITED_EMAIL_DOMAINS_TO_BLOCK), 1000)();
        update(updateValidation(field, {
          data: value
        }));
      },
      onChangeCheckbox: () => update(updateValidation(field, {
        useDefaultBlockList: !field.validation.useDefaultBlockList
      }))
    });
  }
  renderDefaultValueField() {
    const {
      field,
      update
    } = this.props;
    return /*#__PURE__*/_jsx(CommentableArea, {
      moduleId: `${getPropertyObjectTypeId(field)}-default-value`,
      description: {
        translationKey: 'formsDefaultValue'
      },
      children: /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: langKey('defaultValue')
          }),
          tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: langKey('defaultValueToolTip')
          }),
          tooltipPlacement: "top right",
          children: /*#__PURE__*/_jsx(TextInput, {
            defaultValue: field.defaultValue,
            use: "on-dark",
            "data-test-id": "config-field-default-value",
            onChange: ({
              target: {
                value
              }
            }) => {
              debounce(() => trackEditorInteraction(EDITED_DEFAULT_VALUE), 1000)();
              update(updateFormFieldKey(field, FormFieldKeys.DEFAULT_VALUE, value));
            },
            "data-editable-field": "defaultValue"
          })
        })
      })
    });
  }
  render() {
    const {
      field,
      update
    } = this.props;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [this.props.RequiredField, /*#__PURE__*/_jsx(Spacer, {}), this.props.HiddenField, /*#__PURE__*/_jsx(Spacer, {}), this.props.LabelField, /*#__PURE__*/_jsx(Spacer, {}), this.props.HelpTextField, /*#__PURE__*/_jsx(Spacer, {}), this.props.renderPlaceholderField({
        value: field.placeholder,
        update: newValue => update(Object.assign({}, field, {
          placeholder: newValue
        })),
        field
      }), /*#__PURE__*/_jsx(Spacer, {}), this.renderDefaultValueField(), /*#__PURE__*/_jsx(Spacer, {}), this.maybeRenderDomainBlockField(), /*#__PURE__*/_jsx(Spacer, {}), this.props.ProfillingField]
    });
  }
}
const mapStateToProps = state => ({
  hasDomainBlocking: hasEmailDomainBlockingAccess(state)
});
const actionCreators = {};
export default connect(mapStateToProps, actionCreators)(Text);